import React from 'react';
import {
  Focusable,
  Tooltip as AriaTooltip,
  TooltipTrigger as AriaTooltipTrigger,
} from 'react-aria-components';

import { tv } from '../../tv';
import { OVERLAY_OFFSET, RAC_PLACEMENT } from '../overlay/placement';
import { TooltipSeatContext } from './tooltip-seat-context';

/**
 * ComplyKit Tooltip: the name or description an element shows on hover and
 * keyboard focus.
 *
 * The API is shaped by the product's 108 Chakra Tooltip tags across 78 files,
 * and by two decisions those tags kept making inconsistently:
 *
 * - The DELAY POLICY is two-tier and system-owned, never a prop. An explicit
 *   Tooltip warms up over 500ms (a pointer crossing the screen is not a
 *   question); once one has shown, siblings open instantly for as long as the
 *   warmth lasts (React Aria's global warmup/cooldown). IconButton's built-in
 *   label pairing is the other tier: 0ms always, because an icon-only button's
 *   name must be one hover away, not half a second away.
 * - INVERTED vs SURFACED: a tooltip is ephemeral and non-interactive, so it
 *   is an inverted pill (neutral-12 on neutral-1, self-inverting in dark via
 *   the scales) with no border, shadow or arrow. Interactive overlays (Menu,
 *   Popover) are surfaced instead: surface-overlay plus the overlay shadow.
 *   The fill is the affordance split; see overlays.md.
 *
 * Content is a string BY RULING, not a ReactNode: anything richer than a
 * sentence is Popover-shaped (interactive, focusable, dismissed on its own
 * terms), and a tooltip that cannot hold markup cannot grow into one.
 */
export interface TooltipProps {
  /**
   * The tooltip text. A string on purpose: a tooltip names or describes the
   * thing it points at, in one plain sentence. Rich content (headings,
   * links, anything interactive) is Popover-shaped and belongs there.
   */
  content: string;
  /**
   * Where the tooltip sits relative to the trigger. When there is no room on
   * the asked-for side it flips to the opposite one.
   */
  placement?:
    | 'top'
    | 'bottom'
    | 'left'
    | 'right'
    | 'top-start'
    | 'top-end'
    | 'bottom-start'
    | 'bottom-end';
  /** Whether the tooltip is disabled. Renders the trigger bare, with no
   * tooltip behaviour attached at all. */
  isDisabled?: boolean;
  /** Exactly one trigger element. An interactive ComplyKit component wires
   * itself up; a plain element (a span, an abbreviation) is made focusable
   * automatically so the tooltip stays keyboard-reachable. */
  children: React.ReactElement;
}

/* The two-tier delay policy (see the header comment). WARM is the explicit
 * Tooltip's tier; IconButton's built-in pairing passes 0 directly. CLOSE stays
 * at React Aria's 500ms so a pointer that wavers off the trigger does not make
 * the pill flicker, and so a pointer moving over the pill has time to read it
 * before it goes (WCAG 1.4.13). */
const WARM_DELAY = 500;
const CLOSE_DELAY = 500;

/* The inverted pill (locked): neutral-12 on neutral-1 self-inverts in dark
 * because the scales flip; max-w-xs is 320px and the text always wraps, never
 * truncates (a tooltip that truncates has failed at its one job). No border,
 * no shadow, no arrow. The motion is the shared overlay entrance from the
 * theme: fade plus a 2px slide from the trigger side, axis picked by the
 * placement the tooltip actually resolved to (data-placement is the flipped
 * result, not the request).
 *
 * Exported for Menu's unavailable-item reason, which renders the same
 * inverted pill anchored on a menu item (a direct import, never through the
 * barrel, the ButtonSpinner precedent): one pill recipe, so the reason
 * tooltip and the explicit Tooltip cannot drift apart. */
export const tooltipStyles = tv({
  base: [
    /* The pill never sits between the pointer and a control. It opens on the
     * element under a resting pointer, which after a layout shift (a row
     * removed, a dialog closed) can be a neighbour of the control the user
     * is reaching for, and the pill lands over that control. Spectrum draws
     * the same line: a tooltip takes no pointer events. */
    'ck:pointer-events-none',
    'ck:font-sans ck:text-xs ck:max-w-xs',
    'ck:rounded-3 ck:px-2 ck:py-1',
    'ck:bg-neutral-12 ck:text-neutral-1',
    'ck:motion-overlay ck:exiting:motion-overlay-out',
    'ck:entering:opacity-0 ck:exiting:opacity-0',
    'ck:placement-top:entering:overlay-shift-down ck:placement-top:exiting:overlay-shift-down',
    'ck:placement-bottom:entering:overlay-shift-up ck:placement-bottom:exiting:overlay-shift-up',
    'ck:placement-left:entering:overlay-shift-right ck:placement-left:exiting:overlay-shift-right',
    'ck:placement-right:entering:overlay-shift-left ck:placement-right:exiting:overlay-shift-left',
  ],
});

interface TooltipSeatProps {
  content: string;
  placement: NonNullable<TooltipProps['placement']>;
  /** Warm delay in ms: 500 for the explicit Tooltip, 0 for IconButton's
   * built-in pairing. Internal knob; the public interface has no delay. */
  delay: number;
  children: React.ReactElement;
  tooltipRef?: React.ForwardedRef<HTMLDivElement>;
}

/** The shared trigger seat: Tooltip renders it with the warm delay, and
 * IconButton renders it directly for the instant label pairing. A plain
 * function on purpose, shared through a direct import and never through the
 * barrel (the ButtonSpinner precedent). */
export function TooltipSeat({ content, placement, delay, children, tooltipRef }: TooltipSeatProps) {
  /* A component trigger (Button, IconButton) receives the trigger props
   * through React Aria's context; an intrinsic element (span, abbr) consumes
   * no context, so it is wrapped in Focusable, which clones the props on and
   * keeps the trigger keyboard-reachable (tabIndex 0). */
  const trigger =
    typeof children.type === 'string' ? (
      <Focusable>{children as React.ReactElement<React.DOMAttributes<Element>, string>}</Focusable>
    ) : (
      children
    );

  return (
    <AriaTooltipTrigger delay={delay} closeDelay={CLOSE_DELAY}>
      <TooltipSeatContext.Provider value={true}>{trigger}</TooltipSeatContext.Provider>
      <AriaTooltip
        ref={tooltipRef}
        placement={RAC_PLACEMENT[placement]}
        offset={OVERLAY_OFFSET}
        className={tooltipStyles()}
        /* React Aria pins z-index 100000 inline on the positioned element, so
         * the layer token must ride the same inline slot to win; a class
         * cannot. The cast is because csstype does not admit var() for
         * z-index. */
        style={{ zIndex: 'var(--ck-z-overlay)' } as React.CSSProperties}
        data-component="tooltip"
      >
        {content}
      </AriaTooltip>
    </AriaTooltipTrigger>
  );
}

export const Tooltip = React.forwardRef<HTMLDivElement, TooltipProps>(function Tooltip(
  { content, placement = 'top', isDisabled = false, children },
  ref,
) {
  // Disabled means bare: no wrapper, no trigger behaviour, just the child.
  if (isDisabled) return children;

  return (
    <TooltipSeat content={content} placement={placement} delay={WARM_DELAY} tooltipRef={ref}>
      {children}
    </TooltipSeat>
  );
});
